import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Image as ImageIcon,
  Loader2,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  X,
  Eye,
  Edit,
  Save,
  Wand2,
  MessageCircleWarning,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { adminFetch } from '../lib/api';
import { useBackgroundTasks } from '../lib/BackgroundTaskContext';
import { CHAPTER_IDS, CHAPTER_TITLES_DE } from '@shared/chapters';

interface ImageQuestion {
  id: string;
  chapter_id: number;
  difficulty: string;
  type: string;
  points: number;
  question_text_de: string;
  options_de: { id: string; text: string }[];
  explanation_de: string;
  correct_option_ids: string[];
  tags: string[];
  image_placeholder: string | null;
  image_url: string | null;
  visual_prompt: string | null;
}

const CHAPTERS = CHAPTER_IDS;
const CHAPTER_TITLES = CHAPTER_TITLES_DE;
const DIFFICULTIES = ['Leicht', 'Mittel', 'Schwer'];
const TYPES = ['Single Choice', 'Multiple Choice', 'Situation'];

export const ImagesPage: React.FC = () => {
  const [questions, setQuestions] = useState<ImageQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterChapter, setFilterChapter] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<'' | 'with' | 'without'>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [generatingPrompts, setGeneratingPrompts] = useState<number | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [chapterPrompts, setChapterPrompts] = useState<Record<number, string>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState<{ id: string; prompt: string } | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<ImageQuestion | null>(null);
  const [saving, setSaving] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [feedbackCounts, setFeedbackCounts] = useState<Record<string, number>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { tasks, startBulkGeneration } = useBackgroundTasks();
  const bulkGenerating = tasks.some((t) => t.status === 'running');

  // Lazy-load thumbnails via IntersectionObserver
  const thumbnailRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const qId = node.dataset.questionId;
      if (!qId || thumbnails[qId]) return;

      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const id = (entry.target as HTMLElement).dataset.questionId;
                if (id) {
                  adminDb
                    .from('questions')
                    .select('image_url, image_placeholder')
                    .eq('id', id)
                    .single()
                    .then(({ data }) => {
                      const imgSrc = data?.image_url || data?.image_placeholder;
                      if (imgSrc) {
                        setThumbnails((prev) => ({ ...prev, [id]: imgSrc }));
                      }
                    });
                  observerRef.current?.unobserve(entry.target);
                }
              }
            });
          },
          { rootMargin: '200px' }
        );
      }
      observerRef.current.observe(node);
    },
    [thumbnails]
  );

  useEffect(() => {
    loadQuestions();
    loadFeedbackCounts();
  }, []);

  const loadFeedbackCounts = async () => {
    try {
      const { data } = await adminDb.from('image_feedback').select('question_id');
      if (data) {
        const counts: Record<string, number> = {};
        data.forEach((row: any) => {
          counts[row.question_id] = (counts[row.question_id] || 0) + 1;
        });
        setFeedbackCounts(counts);
      }
    } catch {
      /* ignore */
    }
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      // Load with image_url (lightweight URL) instead of image_placeholder (heavy base64)
      const { data, error } = await adminDb
        .from('questions')
        .select(
          'id, chapter_id, difficulty, type, points, question_text_de, options_de, explanation_de, correct_option_ids, tags, visual_prompt, image_url'
        )
        .order('chapter_id', { ascending: true })
        .order('id', { ascending: true });
      if (error) throw error;

      // Check for legacy base64 images (not yet migrated)
      const { data: imgData } = await adminDb.rpc('get_questions_with_images');
      const idsWithLegacyImage = new Set((imgData || []).map((r: any) => r.id));

      const processed = (data || []).map((q) => ({
        ...q,
        image_placeholder: q.image_url
          ? 'has_image'
          : idsWithLegacyImage.has(q.id)
            ? 'has_image'
            : null,
        image_url: q.image_url || null,
      }));
      setQuestions(processed as any);
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Compute per-chapter numbering
  const chapterQuestionNumbers = React.useMemo(() => {
    const map: Record<string, { num: number; total: number }> = {};
    const grouped: Record<number, string[]> = {};
    const sorted = [...questions].sort((a, b) => a.id.localeCompare(b.id));
    sorted.forEach((q) => {
      if (!grouped[q.chapter_id]) grouped[q.chapter_id] = [];
      grouped[q.chapter_id].push(q.id);
    });
    Object.entries(grouped).forEach(([, ids]) => {
      ids.forEach((id, index) => {
        map[id] = { num: index + 1, total: ids.length };
      });
    });
    return map;
  }, [questions]);

  const filtered = React.useMemo(() => {
    let result = [...questions];
    if (filterChapter !== '') result = result.filter((q) => q.chapter_id === filterChapter);
    if (filterStatus === 'with') result = result.filter((q) => q.image_placeholder);
    if (filterStatus === 'without') result = result.filter((q) => !q.image_placeholder);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((q) => q.question_text_de?.toLowerCase().includes(term));
    }
    // Sort by chapter, then by question number within chapter
    result.sort((a, b) => {
      if (a.chapter_id !== b.chapter_id) return a.chapter_id - b.chapter_id;
      const aNum = chapterQuestionNumbers[a.id]?.num || 0;
      const bNum = chapterQuestionNumbers[b.id]?.num || 0;
      return aNum - bNum;
    });
    return result;
  }, [questions, filterChapter, filterStatus, searchTerm, chapterQuestionNumbers]);

  const withImage = questions.filter((q) => q.image_placeholder).length;
  const withoutImage = questions.length - withImage;

  const handleGenerateImage = async (questionId: string, prompt: string, chapterId?: number) => {
    setGeneratingId(questionId);
    setMessage({ text: 'Bild wird generiert...', type: 'info' });
    try {
      const response = await adminFetch(`/api/ai/generate-image`, {
        method: 'POST',
        body: JSON.stringify({ prompt, questionId, chapterId }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server-Fehler ${response.status}`);
      }
      const data = await response.json();
      if (data.image) {
        // Server now saves to Storage + DB automatically
        // Just update the local thumbnail cache
        setThumbnails((prev) => ({ ...prev, [questionId]: data.image }));
      }
      const validInfo = data.validation
        ? data.validation.valid
          ? '✅'
          : `⚠️ ${data.validation.issues}`
        : '';
      setMessage({
        text: `Bild generiert ${validInfo} (Versuch ${data.validation?.attempt || 1})`,
        type: 'success',
      });
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setGeneratingId(null);
    }
  };

  const handleAutoGeneratePrompts = async (chapterId: number) => {
    setGeneratingPrompts(chapterId);
    setMessage({ text: `Prompts für Kapitel ${chapterId} werden generiert...`, type: 'info' });
    try {
      const chapterQuestions = questions.filter(
        (q) => q.chapter_id === chapterId && !q.visual_prompt
      );
      if (chapterQuestions.length === 0) {
        setMessage({ text: 'Alle Fragen in diesem Kapitel haben bereits Prompts.', type: 'info' });
        return;
      }

      // Process in batches of 10
      const batchSize = 10;
      let totalUpdated = 0;

      for (let i = 0; i < chapterQuestions.length; i += batchSize) {
        const batch = chapterQuestions.slice(i, i + batchSize);
        const response = await adminFetch(`/api/ai/generate-prompts`, {
          method: 'POST',
          body: JSON.stringify({ questionIds: batch.map((q) => q.id) }),
        });
        if (response.ok) {
          const data = await response.json();
          totalUpdated += data.updated || 0;
        } else {
          const errData = await response.json().catch(() => ({}));
          console.error(`Prompt generation failed for batch:`, errData);
          setMessage({
            text: `Fehler: ${errData.error || `Server-Fehler ${response.status}`}`,
            type: 'error',
          });
          return;
        }
      }

      setMessage({
        text: `${totalUpdated} Prompts für Kapitel ${chapterId} generiert!`,
        type: 'success',
      });
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setGeneratingPrompts(null);
    }
  };

  const handleBulkGenerate = async (chapterId: number) => {
    const chapterPrompt = chapterPrompts[chapterId]?.trim();
    const chapterQuestions = questions.filter((q) => {
      if (q.chapter_id !== chapterId || q.image_placeholder) return false;
      return q.visual_prompt || chapterPrompt;
    });
    if (chapterQuestions.length === 0) {
      setMessage({ text: 'Keine Fragen ohne Bild (mit Prompt) in diesem Kapitel.', type: 'info' });
      return;
    }
    if (
      !confirm(
        `${chapterQuestions.length} Bilder für Kapitel ${chapterId} – ${CHAPTER_TITLES[chapterId]} generieren? Die Generierung läuft im Hintergrund.`
      )
    )
      return;

    // Prepare question list with prompts
    const questionsWithPrompts = chapterQuestions.map((q) => ({
      id: q.id,
      prompt: q.visual_prompt || `${chapterPrompt}. Kontext der Frage: ${q.question_text_de}`,
    }));

    // Delegate to background context
    startBulkGeneration({
      chapterId,
      chapterTitle: CHAPTER_TITLES[chapterId],
      questions: questionsWithPrompts,
    });

    setMessage({
      text: `Bulk-Generierung für Kapitel ${chapterId} gestartet. Du kannst jetzt weiter arbeiten!`,
      type: 'success',
    });
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <ImageIcon size={24} /> Bild-Management
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <ImageIcon size={24} /> Bild-Management
        </h1>
        <button className="btn btn-secondary btn-sm" onClick={loadQuestions}>
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'error' ? 'danger' : 'info'} mb-4`}
        >
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span>{message.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Background Task Progress (if on this page) */}
      {tasks.filter((t) => t.status === 'running').length > 0 && (
        <div className="alert alert-info mb-4">
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>
            Hintergrund-Generierung läuft (
            {tasks
              .filter((t) => t.status === 'running')
              .map((t) => `${t.label}: ${t.current}/${t.total}`)
              .join(', ')}
            )
          </span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Fragen gesamt</span>
          <span className="kpi-value">{questions.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Mit Bild</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>
            {withImage}
          </span>
          <span className="text-xs text-muted">
            {questions.length > 0 ? Math.round((withImage / questions.length) * 100) : 0}%
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ohne Bild</span>
          <span className="kpi-value" style={{ color: 'var(--warning)' }}>
            {withoutImage}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Mit Prompt (ohne Bild)</span>
          <span className="kpi-value">
            {questions.filter((q) => !q.image_placeholder && q.visual_prompt).length}
          </span>
          <span className="text-xs text-muted">Bereit zur Generierung</span>
        </div>
      </div>

      {/* Bulk Generate per Chapter */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 'var(--space-4)' }}>Bulk-Generierung pro Kapitel</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {CHAPTERS.map((ch) => {
            const chQ = questions.filter((q) => q.chapter_id === ch);
            const total = chQ.length;
            const withImage = chQ.filter((q) => q.image_placeholder).length;
            const missing = total - withImage;
            const withPrompt = chQ.filter((q) => !q.image_placeholder && q.visual_prompt).length;
            const withoutPrompt = chQ.filter(
              (q) => !q.image_placeholder && !q.visual_prompt
            ).length;
            const chPrompt = chapterPrompts[ch] || '';
            const canGenerate = withPrompt > 0 || (withoutPrompt > 0 && chPrompt.trim());
            return (
              <div
                key={ch}
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  background:
                    missing === 0 ? 'var(--success-bg, rgba(34,197,94,0.08))' : 'var(--surface)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: missing > 0 && withoutPrompt > 0 ? 8 : 0,
                  }}
                >
                  <div
                    style={{
                      minWidth: 36,
                      textAlign: 'center',
                      fontWeight: 700,
                      color: 'var(--primary)',
                    }}
                  >
                    {ch}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{CHAPTER_TITLES[ch]}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {withImage}/{total} Bilder •{' '}
                      {missing === 0
                        ? '✅ Komplett'
                        : `${withPrompt} mit Prompt, ${withoutPrompt} ohne Prompt`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {withoutPrompt > 0 && (
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={generatingPrompts === ch}
                        onClick={() => handleAutoGeneratePrompts(ch)}
                        style={{ whiteSpace: 'nowrap' }}
                        title={`${withoutPrompt} Prompts automatisch generieren`}
                      >
                        {generatingPrompts === ch ? (
                          <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Wand2 size={12} />
                        )}{' '}
                        {withoutPrompt} Prompts
                      </button>
                    )}
                    <button
                      className={`btn btn-sm ${missing === 0 ? 'btn-secondary' : 'btn-primary'}`}
                      disabled={!canGenerate || bulkGenerating}
                      onClick={() => handleBulkGenerate(ch)}
                      style={{ minWidth: 100, whiteSpace: 'nowrap' }}
                      title={
                        missing === 0
                          ? 'Alle Bilder vorhanden'
                          : `${missing} fehlende Bilder generieren`
                      }
                    >
                      {missing === 0 ? '✅ Fertig' : `${missing} generieren`}
                    </button>
                  </div>
                </div>
                {missing > 0 && withoutPrompt > 0 && (
                  <textarea
                    className="input"
                    rows={3}
                    style={{ width: '100%', fontSize: 12, resize: 'vertical', marginTop: 4 }}
                    placeholder={`Kapitel-Prompt für ${withoutPrompt} Fragen ohne eigenen Prompt…\nz.B. "Realistische deutsche Verkehrsszene, professionelle Illustration, klare Details"`}
                    value={chPrompt}
                    onChange={(e) => setChapterPrompts((p) => ({ ...p, [ch]: e.target.value }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter + Table */}
      <div className="table-container">
        <div className="table-toolbar">
          <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: 200 }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                className="input input-search"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="input"
              style={{ width: 140 }}
              value={filterChapter}
              onChange={(e) => setFilterChapter(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Alle Kapitel</option>
              {CHAPTERS.map((ch) => (
                <option key={ch} value={ch}>
                  Kapitel {ch}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 130 }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <option value="">Alle</option>
              <option value="with">Mit Bild</option>
              <option value="without">Ohne Bild</option>
            </select>
          </div>
          <span className="text-xs text-muted">{filtered.length} Ergebnisse</span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
            padding: 16,
          }}
        >
          {filtered.map((q) => {
            const qNum = chapterQuestionNumbers[q.id];
            return (
              <div
                key={q.id}
                className="card"
                style={{ padding: 12, display: 'flex', flexDirection: 'column' }}
              >
                {/* Thumbnail */}
                <div
                  ref={q.image_placeholder ? thumbnailRef : undefined}
                  data-question-id={q.id}
                  style={{
                    width: '100%',
                    height: 140,
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: q.image_placeholder ? 'var(--surface)' : 'var(--background)',
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${q.image_placeholder ? 'var(--success)' : 'var(--border)'}`,
                    cursor: q.image_placeholder ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                  onClick={async () => {
                    if (!q.image_placeholder) return;
                    if (thumbnails[q.id]) {
                      setPreviewImage(thumbnails[q.id]);
                      return;
                    }
                    setMessage({ text: 'Bild wird geladen...', type: 'info' });
                    const { data } = await adminDb
                      .from('questions')
                      .select('image_placeholder')
                      .eq('id', q.id)
                      .single();
                    if (data?.image_placeholder) {
                      setPreviewImage(data.image_placeholder);
                      setThumbnails((prev) => ({ ...prev, [q.id]: data.image_placeholder }));
                      setMessage(null);
                    }
                  }}
                >
                  {q.image_placeholder && thumbnails[q.id] ? (
                    <img
                      src={thumbnails[q.id]}
                      alt="Vorschau"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : q.image_placeholder ? (
                    <div
                      className="flex flex-col items-center gap-1"
                      style={{ color: 'var(--success)' }}
                    >
                      <Loader2
                        size={20}
                        style={{ animation: 'spin 1s linear infinite', opacity: 0.5 }}
                      />
                      <span className="text-xs" style={{ fontWeight: 600 }}>
                        Lädt Vorschau…
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-muted">
                      <ImageIcon size={20} />
                      <span className="text-xs">Kein Bild</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="badge badge-neutral" style={{ fontSize: 10 }}>
                    Kap. {q.chapter_id}
                  </span>
                  <span className="text-xs" style={{ fontWeight: 600 }}>
                    #{qNum?.num || '—'}/{qNum?.total || '—'}
                  </span>
                  {q.image_placeholder ? (
                    <span className="badge badge-success" style={{ fontSize: 10 }}>
                      <CheckCircle size={8} /> ✓
                    </span>
                  ) : (
                    <span className="badge badge-warning" style={{ fontSize: 10 }}>
                      Fehlt
                    </span>
                  )}
                  {feedbackCounts[q.id] > 0 && (
                    <span
                      className="badge"
                      style={{
                        fontSize: 10,
                        background: '#f97316',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      <MessageCircleWarning size={9} /> {feedbackCounts[q.id]}
                    </span>
                  )}
                </div>
                <p className="text-sm" style={{ lineHeight: 1.4, marginBottom: 4, flex: 1 }}>
                  {q.question_text_de}
                </p>

                {/* Prompt indicator */}
                {q.visual_prompt ? (
                  <p
                    className="text-xs text-muted"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4,
                    }}
                    title={q.visual_prompt}
                  >
                    📝 {q.visual_prompt}
                  </p>
                ) : (
                  <p className="text-xs" style={{ color: 'var(--warning)', marginBottom: 4 }}>
                    ⚠️ Kein Prompt
                  </p>
                )}

                {/* Actions — fixed height row */}
                <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={generatingId === q.id || !q.visual_prompt}
                    onClick={() =>
                      q.visual_prompt
                        ? handleGenerateImage(q.id, q.visual_prompt!, q.chapter_id)
                        : setEditPrompt({ id: q.id, prompt: '' })
                    }
                    style={{ flex: 1, minHeight: 32 }}
                    title={!q.visual_prompt ? 'Zuerst einen Prompt hinzufügen' : ''}
                  >
                    {generatingId === q.id ? (
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <ImageIcon size={12} />
                    )}
                    {q.image_placeholder ? ' Neu' : ' Gen.'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditPrompt({ id: q.id, prompt: q.visual_prompt || '' })}
                    style={{ minHeight: 32 }}
                  >
                    📝
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Frage bearbeiten"
                    onClick={() => setEditingQuestion({ ...q })}
                    style={{ minHeight: 32 }}
                  >
                    <Edit size={12} /> Bearbeiten
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            Keine Fragen gefunden.
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Vorschau"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }}
          />
        </div>
      )}

      {/* Prompt Editor Modal */}
      {editPrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setEditPrompt(null)}
        >
          <div
            className="card"
            style={{ width: 500, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 16 }}>Bild-Prompt bearbeiten</h3>
            <textarea
              className="input"
              rows={4}
              value={editPrompt.prompt}
              onChange={(e) => setEditPrompt({ ...editPrompt, prompt: e.target.value })}
              placeholder="Beschreibe die gewuenschte Verkehrsszene..."
            />
            <div className="flex justify-between mt-4">
              <button className="btn btn-secondary" onClick={() => setEditPrompt(null)}>
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  await adminDb
                    .from('questions')
                    .update({ visual_prompt: editPrompt.prompt })
                    .eq('id', editPrompt.id);
                  setEditPrompt(null);
                  setMessage({ text: 'Prompt gespeichert.', type: 'success' });
                  await loadQuestions();
                }}
              >
                Speichern & Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Question Editor Modal */}
      {editingQuestion &&
        (() => {
          const q = editingQuestion;
          const setQ = (updated: ImageQuestion) => setEditingQuestion(updated);
          const updateOption = (index: number, text: string) => {
            const opts = [...(q.options_de || [])];
            opts[index] = { ...opts[index], text };
            setQ({ ...q, options_de: opts });
          };
          const toggleCorrect = (optId: string) => {
            const ids = q.correct_option_ids.includes(optId)
              ? q.correct_option_ids.filter((id: string) => id !== optId)
              : [...q.correct_option_ids, optId];
            setQ({ ...q, correct_option_ids: ids });
          };
          const handleSave = async () => {
            setSaving(true);
            try {
              const { error } = await adminDb.from('questions').upsert({
                id: q.id,
                chapter_id: q.chapter_id,
                difficulty: q.difficulty,
                type: q.type,
                points: q.points,
                question_text_de: q.question_text_de,
                options_de: q.options_de,
                explanation_de: q.explanation_de,
                correct_option_ids: q.correct_option_ids,
                tags: q.tags,
                image_placeholder: q.image_placeholder,
                visual_prompt: q.visual_prompt,
              });
              if (error) throw error;
              setMessage({ text: 'Frage gespeichert.', type: 'success' });
              setEditingQuestion(null);
              await loadQuestions();
            } catch (err: any) {
              setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
            } finally {
              setSaving(false);
            }
          };
          return (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: '40px 20px',
                overflowY: 'auto',
              }}
              onClick={() => setEditingQuestion(null)}
            >
              <div
                style={{
                  background: 'var(--surface)',
                  borderRadius: 'var(--radius-xl)',
                  width: '100%',
                  maxWidth: 720,
                  padding: 32,
                  boxShadow: 'var(--shadow-lg)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2>Frage bearbeiten</h2>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingQuestion(null)}>
                    <X size={18} />
                  </button>
                </div>
                <div className="grid-cols-3 mb-4">
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Kapitel
                    </label>
                    <select
                      className="input"
                      value={q.chapter_id}
                      onChange={(e) => setQ({ ...q, chapter_id: Number(e.target.value) })}
                    >
                      {CHAPTERS.map((ch) => (
                        <option key={ch} value={ch}>
                          Kapitel {ch}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Schwierigkeit
                    </label>
                    <select
                      className="input"
                      value={q.difficulty}
                      onChange={(e) => setQ({ ...q, difficulty: e.target.value })}
                    >
                      {DIFFICULTIES.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Typ
                    </label>
                    <select
                      className="input"
                      value={q.type}
                      onChange={(e) => setQ({ ...q, type: e.target.value })}
                    >
                      {TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Fehlerpunkte
                  </label>
                  <input
                    className="input"
                    type="number"
                    min={2}
                    max={5}
                    value={q.points}
                    onChange={(e) => setQ({ ...q, points: Number(e.target.value) })}
                    style={{ width: 80 }}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Fragetext (DE)
                  </label>
                  <textarea
                    className="input"
                    rows={3}
                    value={q.question_text_de || ''}
                    onChange={(e) => setQ({ ...q, question_text_de: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Antwortoptionen
                  </label>
                  <div className="flex flex-col gap-2">
                    {(q.options_de || []).map((opt: any, i: number) => (
                      <div key={opt.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={q.correct_option_ids.includes(opt.id)}
                          onChange={() => toggleCorrect(opt.id)}
                          title="Korrekte Antwort"
                        />
                        <span className="text-xs" style={{ fontWeight: 600, width: 20 }}>
                          {opt.id})
                        </span>
                        <input
                          className="input"
                          value={opt.text}
                          onChange={(e) => updateOption(i, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted mt-2">Checkbox = korrekte Antwort</p>
                </div>
                <div className="mb-4">
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Erklaerung
                  </label>
                  <textarea
                    className="input"
                    rows={2}
                    value={q.explanation_de || ''}
                    onChange={(e) => setQ({ ...q, explanation_de: e.target.value })}
                  />
                </div>
                <div className="mb-6">
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Bild-Prompt
                  </label>
                  <input
                    className="input"
                    value={q.visual_prompt || ''}
                    onChange={(e) => setQ({ ...q, visual_prompt: e.target.value })}
                    placeholder="z.B. Kreuzung mit Vorfahrtsschild..."
                  />
                </div>
                {q.image_placeholder && (
                  <div className="mb-6">
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Aktuelles Bild
                    </label>
                    <img
                      src={
                        q.image_placeholder.startsWith('data:') ||
                        q.image_placeholder.startsWith('http')
                          ? q.image_placeholder
                          : ''
                      }
                      alt="Fragebild"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        borderRadius: 8,
                        border: '1px solid var(--border)',
                      }}
                    />
                  </div>
                )}
                <div className="flex justify-between">
                  <button className="btn btn-secondary" onClick={() => setEditingQuestion(null)}>
                    Abbrechen
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Save size={16} />
                    )}
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
